import { FileText, Plus } from "lucide-react";
import { useState } from "react";

import Loader from "../../../components/Loader";
import { Button } from "../../../components/ui/button";
import { buildDocumentUrl } from "../../../handlers/documentStorage";
import { useSmartProfile } from "../../../services/create-resume/queries";

interface DSP {
	dsp_id: string;
	dsp_title: string;
	dsp_description: string;
	source_resume: {
		doc_id: string;
		file_name: string;
		storage_key: string;
	};
}

interface SelectDSPProps {
	runFitCheck: (resumeURL: string) => void;
}

function SelectDSP({ runFitCheck }: SelectDSPProps) {
	const { data: smartProfileData, isLoading } = useSmartProfile();
	const [selectedResume, setSelectedResume] = useState<string | null>(null);

	const dsps: DSP[] = smartProfileData?.dsps || [];

	const handleSelectDSP = (resumeURL: string) => {
		setSelectedResume(resumeURL);
	};

	const handleRunFitCheck = () => {
		if (selectedResume) {
			const fullResumeURL = buildDocumentUrl(selectedResume);

			// TODO: Move to next step
			runFitCheck(fullResumeURL);
		}
	};

	const handleCreateNewDSP = () => {
		console.log("Create new DSP clicked");
		// TODO: Navigate to DSP creation
	};

	if (isLoading) {
		return (
			<div className="flex items-center justify-center min-h-[400px]">
				<Loader />
			</div>
		);
	}

	// Empty state
	if (dsps.length === 0) {
		return (
			<div className="max-w-2xl">
				<div className="bg-blue-50 rounded-lg p-8 text-center">
					<div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-6">
						<FileText className="w-10 h-10 text-blue-600" strokeWidth={1.5} />
					</div>
					<h2 className="text-xl font-semibold text-gray-900 mb-3">You don&apos;t have a DSP yet.</h2>
					<p className="text-gray-700 mb-8 max-w-md mx-auto">
						Your Smart Profile (DSP) helps us analyze your fit accurately. Create one by uploading a resume to unlock
						your personalized FitCheck results.
					</p>
					<button
						type="button"
						onClick={handleCreateNewDSP}
						className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-md text-base font-medium transition-colors"
					>
						Create DSP
					</button>
				</div>
			</div>
		);
	}

	return (
		<div className="max-w-2xl">
			<div className="mb-6">
				<h1 className="text-2xl font-bold text-gray-900 mb-2">Choose a resume to compare</h1>
				<p className="text-gray-600">Select the Smart Profile that best matches the role you want to FitCheck.</p>
			</div>

			<div className="space-y-3 mb-6">
				{dsps.map((dsp) => (
					<div
						key={dsp.dsp_id}
						onClick={() => handleSelectDSP(dsp.source_resume.storage_key)}
						className={`
              relative p-4 rounded-lg border-2 cursor-pointer transition-all
              ${
								selectedResume === dsp.dsp_id
									? "border-blue-600 bg-blue-50"
									: "border-gray-200 bg-white hover:border-gray-300"
							}
            `}
					>
						<div className="flex items-start justify-between">
							<div className="flex-1">
								<h3 className="text-lg font-semibold text-gray-900 mb-1">{dsp.dsp_title}</h3>
								<p className="text-sm text-gray-600 mb-2">{dsp.dsp_description || "No description"}</p>
								<div className="flex items-center gap-1 text-sm text-gray-500">
									<FileText className="w-4 h-4" />
									<span>Resume: {dsp.source_resume.file_name}</span>
								</div>
							</div>

							{selectedResume === dsp.dsp_id && (
								<div className="shrink-0 ml-4">
									<div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
										<svg
											className="w-4 h-4 text-white"
											fill="none"
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth="2"
											viewBox="0 0 24 24"
											stroke="currentColor"
										>
											<path d="M5 13l4 4L19 7" />
										</svg>
									</div>
								</div>
							)}
						</div>
					</div>
				))}
			</div>

			<button
				type="button"
				onClick={handleCreateNewDSP}
				className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium mb-8"
			>
				<Plus className="w-5 h-5" />
				Create New DSP
			</button>

			<Button className="w-full" onClick={handleRunFitCheck} disabled={!selectedResume}>
				Run FitCheck
			</Button>
		</div>
	);
}

export default SelectDSP;
